import { Edge, Node } from '@xyflow/react';
import { IOrganizationChartNode } from '../../../../../app/resource/node/node.types';
import {
  chartNodeParentIdList,
  NODE_HEIGHT,
  NODE_WIDTH,
  getNodePosition,
} from '../../../../../app/resource/node/node.utils';
import { NodePosition } from '../../../../../app/resource/node/node.types';

/** Footer strip for admin “add seat” control; match `.orgchart-node-wrap` padding-bottom in theme. */
const ORG_CHART_FLOW_NODE_EXTRA_HEIGHT = 36;
const ORG_CHART_FLOW_NODE_HEIGHT = NODE_HEIGHT + ORG_CHART_FLOW_NODE_EXTRA_HEIGHT;

/** Class on the seat card only; footer button is outside this surface so it stays clickable (`dragHandle`). */
export const ORG_CHART_NODE_DRAG_HANDLE_CLASS = 'orgchart-drag-surface';

export type OrgChartNodeData = { nodeData: IOrganizationChartNode; chartType: string };
export type OrgChartNodeType = Node<OrgChartNodeData, 'orgChartNode'>;

/** Builds React Flow nodes and edges from chart nodes using resolved layout positions. */
export function buildReactFlowNodesAndEdges(
  nodes: IOrganizationChartNode[],
  chartType: string,
): { rfNodes: OrgChartNodeType[]; rfEdges: Edge[] } {
  const resolvedPositions = buildResolvedPositions(nodes);
  const nodeIdSetInChart = new Set(nodes.map((node) => String(node.id)));

  const rfNodes: OrgChartNodeType[] = nodes.flatMap((node) => {
    const position = resolvedPositions[String(node.id)];
    if (!position) {
      return [];
    }
    return [
      {
        id: String(node.id),
        type: 'orgChartNode',
        position,
        data: { nodeData: node, chartType },
        selected: false,
        width: NODE_WIDTH,
        // height: ORG_CHART_FLOW_NODE_HEIGHT,
        dragHandle: `.${ORG_CHART_NODE_DRAG_HANDLE_CLASS}`,
        style: { backgroundColor: 'transparent', borderRadius: 10 },
      },
    ];
  });

  const nodeIdSetInFlow = new Set(rfNodes.map((node) => node.id));
  const rfEdges: Edge[] = nodes
    .flatMap((node) => {
      const parentIds = chartNodeParentIdList(node).filter((parentId) => nodeIdSetInChart.has(String(parentId)));
      return parentIds.map((parentId, index) => ({
        id: `e-${parentId}-${node.id}-${index}`,
        source: String(parentId),
        target: String(node.id),
        type: 'smoothstep' as const,
        style: { stroke: '#b1b1b7', strokeWidth: 3 },
      }));
    })
    .filter((edge) => nodeIdSetInFlow.has(edge.source) && nodeIdSetInFlow.has(edge.target));

  return { rfNodes, rfEdges };
}

type PositionsById = Record<string, NodePosition>;

/** Resolves flow positions from persisted node coordinates. */
export function buildResolvedPositions(nodes: IOrganizationChartNode[]): PositionsById {
  const resolved: PositionsById = {};
  for (const node of nodes) {
    resolved[String(node.id)] = getNodePosition(node);
  }
  return resolved;
}
